import React, { useState, useEffect } from 'react';
import './Gamecast.css';

const Gamecast = ({ thisDrive, thisPlay, logo, possession, playBefore, hc, ac }) => {
    if (!thisPlay || thisPlay.text === "END GAME") return null;

    const kickoff = !!playBefore?.scoringPlay;

    // Ball marker for first down line
    let fdx = -1;
    if (thisPlay?.end) {
        fdx = (thisPlay.end.yardLine - thisPlay.end.distance) * 3 + 30;
        if (possession === "HOME-POSSESSION") {
            fdx = (thisPlay.end.yardLine + thisPlay.end.distance) * 3 + 30;
        }
    }

    return (
        <div className="Gamecast">
            <hr />
            <div className="important-info">
                <InfoBlock title="DOWN" value={thisPlay?.end?.shortDownDistanceText ?? "-"} />
                <Divider />
                <InfoBlock title="BALL ON" value={thisPlay?.end?.possessionText ?? "-"} />
                <Divider />
                <InfoBlock
                    title="DRIVE"
                    value={`${thisDrive?.offensivePlays ?? 0} Plays, ${thisDrive?.yards ?? 0} Yds`}
                />
            </div>
            <hr />

            <div className="playingField">
                <svg className="field" width="360" height="100">
                    <g className="fieldDesign">
                        <Lines />
                        <Hashes />
                        {fdx > 30 && fdx < 330 && !kickoff && (
                            <line className="firstDown" x1={fdx} y1={0} x2={fdx} y2={70} />
                        )}
                        <Endzone hc={hc} ac={ac} />
                    </g>

                    <g className="lastPlay">
                        {playBefore &&
                            playBefore.type.text !== "Kickoff" &&
                            !kickoff && (
                                <PlayComponent
                                    thisDrive={thisDrive}
                                    thisPlay={playBefore}
                                    logo={logo}
                                    possession={possession}
                                    isLogo={false}
                                />
                            )}
                    </g>

                    <g className="currentPlay">
                        <PlayComponent
                            thisDrive={thisDrive}
                            thisPlay={thisPlay}
                            logo={logo}
                            possession={possession}
                            isLogo={true}
                        />
                    </g>
                </svg>
            </div>

            <div className="playDescription">
                {!kickoff ? (
                    <div>
                        {thisPlay?.start?.shortDownDistanceText
                            ? `${thisPlay.start.shortDownDistanceText} - `
                            : ""}
                        {thisPlay?.text}
                    </div>
                ) : (
                    <div>Kickoff</div>
                )}
            </div>
        </div>
    );
};

// ----------------- Subcomponents -----------------
const InfoBlock = ({ title, value }) => (
    <div>
        <h5>{title}</h5>
        <h4>{value}</h4>
    </div>
);

const Divider = () => <div className="divider"></div>;

const Lines = () =>
    [...Array(11)].map((_, i) => (
        <line key={`line-${i}`} x1={30 + i * 30} y1={0} x2={30 + i * 30} y2={70} />
    ));

const Hashes = () =>
    [...Array(100)].map((_, i) => (
        <g key={`hash-${i}`}>
            <line
                x1={33 + i * 3}
                y1={2}
                x2={33 + i * 3}
                y2={5 + ((i + 1) % 5 === 0 ? 3 : 0)}
            />
            <line
                x1={33 + i * 3}
                y1={65 - ((i + 1) % 5 === 0 ? 3 : 0)}
                x2={33 + i * 3}
                y2={68}
            />
        </g>
    ));

const Endzone = ({ hc, ac }) => (
    <>
        <rect x="0" y="0" width="30" height="80" stroke="white" fill={`#${hc}`} />
        <rect x="330" y="0" width="90" height="80" stroke="white" fill={`#${ac}`} />
    </>
);

// ----------------- PlayComponent -----------------
const PlayComponent = ({ thisDrive, thisPlay, logo, possession, isLogo }) => {
    const [position, setPosition] = useState(thisPlay?.end?.yardLine * 3 + 30);
    const [ground, setGround] = useState(null);
    const [air, setAir] = useState(null);
    const [arc, setArc] = useState(null);
    const [playType, setPlayType] = useState("");

    useEffect(() => {
        if (!thisPlay) return;

        let startY = thisPlay.start.yardLine * 3 + 30;
        let endY = thisPlay.end.yardLine * 3 + 30;

        // Handle scoring play positioning
        if (thisPlay.scoringPlay) {
            endY = possession === "HOME-POSSESSION" ? (360 + 330) / 2 : (0 + 30) / 2;
        }

        setPosition(endY);
        setGround(null);
        setAir(null);
        setArc(null);

        // Passing / kicking plays with arcs
        if (
            ["Pass Reception", "Punt", "Field Goal Good", "Kickoff"].includes(
                thisPlay.type.text
            )
        ) {
            setPlayType(thisPlay.type.abbreviation);
            setAir({ start: startY, end: endY });
            setArc({
                c1: (startY * 2 + endY) / 3,
                c2: (startY + endY * 2) / 3,
            });
        } else {
            // Running plays = straight ground line
            setGround({ start: startY, end: endY });
        }
    }, [thisPlay, possession]);

    return (
        <>
            <circle cx={position} cy={50} r={2} />

            {ground && (
                <line
                    x1={ground.start}
                    y1={50}
                    x2={ground.end}
                    y2={50}
                    style={{
                        stroke: "black",
                        strokeWidth: 2,
                        strokeDasharray: Math.abs(ground.start - ground.end),
                        strokeDashoffset: Math.abs(ground.start - ground.end),
                    }}
                />
            )}

            {air && arc && (
                <path
                    className={`Arc ${playType}`}
                    d={`M ${air.start} 50 C ${arc.c1} 0 ${arc.c2} 0 ${air.end} 50`}
                    style={{
                        stroke: "black",
                        strokeWidth: 2,
                        fill: "none",
                        strokeDasharray:
                            calculateBezierLength(air.start, arc.c1, arc.c2, air.end) + "px",
                        strokeDashoffset:
                            calculateBezierLength(air.start, arc.c1, arc.c2, air.end) + "px",
                    }}
                />
            )}

            {isLogo && (
                <g className="logo">
                    <circle cx={position} cy={35} r={11} />
                    <image href={logo} x={position - 10} y="25" width="20" height="20" />
                </g>
            )}
        </>
    );
};

export { Gamecast };

// ----------------- Utils -----------------
function calculateBezierLength(p0, p1, p2, p3) {
    const steps = 200;
    let length = 0;

    function bezier(t, a, b, c, d) {
        const mt = 1 - t;
        return (
            mt * mt * mt * a +
            3 * mt * mt * t * b +
            3 * mt * t * t * c +
            t * t * t * d
        );
    }

    let prev = { x: p0, y: 50 };
    for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        const curr = { x: bezier(t, p0, p1, p2, p3), y: bezier(t, 50, 0, 0, 50) };
        length += Math.hypot(curr.x - prev.x, curr.y - prev.y);
        prev = curr;
    }

    return length;
}
